// App shell: wraps everything in the site-config + auth providers, defines
// every route, and gates the /app/* portal behind login via <Protected>.
// One page component per route (frontend/src/pages/*.jsx) — role-specific
// behavior within a shared route (e.g. teacher vs. student view of the
// same page) is handled inside each page component, not by having
// separate routes per role.
import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SiteConfigProvider } from "@/context/SiteConfigContext";
import PortalLayout from "@/components/PortalLayout";
import Landing from "@/pages/Landing";
import TeachersPublic from "@/pages/TeachersPublic";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import CoursesPage from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import LessonPage from "@/pages/LessonPage";
import RecordingPage from "@/pages/RecordingPage";
import AttendancePage from "@/pages/AttendancePage";
import LiveClasses from "@/pages/LiveClasses";
import LiveClassJoinPage from "@/pages/LiveClassJoinPage";
import TestsPage from "@/pages/Tests";
import TakeTest from "@/pages/TakeTest";
import TestBuilder from "@/pages/TestBuilder";
import TestResults from "@/pages/TestResults";
import TestReview from "@/pages/TestReview";
import Certificate from "@/pages/Certificate";
import Leaderboard from "@/pages/Leaderboard";
import DailyPerformance from "@/pages/DailyPerformance";
import DailyPerformanceBuilder from "@/pages/DailyPerformanceBuilder";
import TakeDailyPerformance from "@/pages/TakeDailyPerformance";
import DailyPerformanceResults from "@/pages/DailyPerformanceResults";
import DailyPerformanceReview from "@/pages/DailyPerformanceReview";
import DailyPerformanceLeaderboard from "@/pages/DailyPerformanceLeaderboard";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AssignmentsPage from "@/pages/Assignments";
import AnnouncementsPage from "@/pages/Announcements";
import AdminUsers from "@/pages/AdminUsers";
import AdminPayments from "@/pages/AdminPayments";
import AdminTeachers from "@/pages/AdminTeachers";
import AdminTopPerformers from "@/pages/AdminTopPerformers";
import AdminSettings from "@/pages/AdminSettings";
import AdminEnrollments from "@/pages/AdminEnrollments";
import AdminSiteContent from "@/pages/AdminSiteContent";
import AdminTeacherProfiles from "@/pages/AdminTeacherProfiles";
import TeacherProfileEdit from "@/pages/TeacherProfileEdit";
import StudentsProgress from "@/pages/StudentsProgress";
import StudentsProgressCourse from "@/pages/StudentsProgressCourse";
import StudentProgressDetail from "@/pages/StudentProgressDetail";

function Protected({ children }) {
  // Route guard: only checks "is someone logged in?" — role-specific
  // access control happens inside individual page components (e.g. an
  // admin-only page checks user.role itself rather than a dedicated route wrapper).
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-zinc-500" data-testid="app-loading">
        Loading…
      </div>
    );
  if (!user) return <Navigate to="/auth?mode=login" replace />;
  return children;
}

function App() {
  return (
    <SiteConfigProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Fully public routes — no login required */}
            <Route path="/" element={<Landing />} />
            <Route path="/teachers" element={<TeachersPublic />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/certificate/:courseId"
              element={
                <Protected>
                  <Certificate />
                </Protected>
              }
            />
            {/* Everything under /app/* requires login and shares the
                PortalLayout chrome (sidebar/nav) as a parent route */}
            <Route
              path="/app"
              element={
                <Protected>
                  <PortalLayout />
                </Protected>
              }
            >
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="courses" element={<CoursesPage />} />
              <Route path="courses/:id" element={<CourseDetail />} />
              <Route path="courses/:courseId/lessons/:lessonId" element={<LessonPage />} />
              <Route path="live" element={<LiveClasses />} />
              <Route path="live/:id/recording" element={<RecordingPage />} />
              <Route path="live/:id/attendance" element={<AttendancePage />} />
              <Route path="live/:id/join" element={<LiveClassJoinPage />} />
              <Route path="tests" element={<TestsPage />} />
              <Route path="tests/new" element={<TestBuilder />} />
              <Route path="tests/:id/edit" element={<TestBuilder />} />
              <Route path="tests/:id/take" element={<TakeTest />} />
              <Route path="tests/:id/results" element={<TestResults />} />
              <Route path="tests/:id/review" element={<TestReview />} />
              <Route path="tests/:id/leaderboard" element={<Leaderboard />} />
              <Route path="daily-performance" element={<DailyPerformance />} />
              <Route path="daily-performance/new" element={<DailyPerformanceBuilder />} />
              <Route path="daily-performance/:id/edit" element={<DailyPerformanceBuilder />} />
              <Route path="daily-performance/:id/take" element={<TakeDailyPerformance />} />
              <Route path="daily-performance/:id/results" element={<DailyPerformanceResults />} />
              <Route path="daily-performance/:id/review" element={<DailyPerformanceReview />} />
              <Route path="daily-performance/:id/leaderboard" element={<DailyPerformanceLeaderboard />} />
              <Route path="assignments" element={<AssignmentsPage />} />
              <Route path="announcements" element={<AnnouncementsPage />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="teachers" element={<AdminTeachers />} />
              <Route path="top-performers" element={<AdminTopPerformers />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="site-content" element={<AdminSiteContent />} />
              <Route path="teacher-profiles" element={<AdminTeacherProfiles />} />
              <Route path="my-profile" element={<TeacherProfileEdit />} />
              <Route path="enrollments" element={<AdminEnrollments />} />
              <Route path="students-progress" element={<StudentsProgress />} />
              <Route path="students-progress/:courseId" element={<StudentsProgressCourse />} />
              <Route path="students-progress/:courseId/:studentId" element={<StudentProgressDetail />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </SiteConfigProvider>
  );
}

export default App;
